import type {BridgeComponent, BridgeComponentConstructor} from '../types';

type ReactDeps = {React: any};

/**
 * Creates a React driver for a bridge component.
 * Returns a forwardRef component that:
 * - On mount: creates bridge instance, renders into container div
 * - On update: calls updateProps with new props
 * - On unmount: calls destroy
 * - Exposes bridge instance via ref (needed for CardInput's createToken)
 */
export function createReactDriver<P extends Record<string, any>>(
  ComponentClass: BridgeComponentConstructor<P>
): (deps: ReactDeps) => any {
  return ({React}: ReactDeps) => {
    return React.forwardRef(function BridgeReactWrapper(props: P, ref: any) {
      const containerRef = React.useRef(null) as {current: HTMLDivElement | null};
      const instanceRef = React.useRef(null) as {current: BridgeComponent<P> | null};
      const isMounted = React.useRef(false) as {current: boolean};
      const prevPropsRef = React.useRef({} as Record<string, any>);

      // Mount: create bridge instance + render into container div
      // useLayoutEffect to append SDK container BEFORE browser paint (prevents layout jump)
      React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let cancelled = false;
        const instance = new ComponentClass({...props} as P);
        instanceRef.current = instance;

        instance
          .render(el)
          .then(() => {
            if (cancelled) return;
            // Only expose ref after render completes (connection established)
            setRef(ref, instance);
            isMounted.current = true;
          })
          .catch((err: unknown) => {
            if (!cancelled) console.error(err);
          });

        return () => {
          cancelled = true;
          instance.destroy();
          instanceRef.current = null;
          isMounted.current = false;
          setRef(ref, null);
        };
      }, []); // eslint-disable-line react-hooks/exhaustive-deps

      // Update: sync prop changes to bridge component (like componentDidUpdate)
      React.useEffect(() => {
        if (!isMounted.current || !instanceRef.current) return;
        if (shallowEqual(prevPropsRef.current, props)) return;
        prevPropsRef.current = {...props};
        instanceRef.current.updateProps({...props} as Partial<P>).catch(noop);
      });

      return React.createElement('div', {ref: containerRef});
    });
  };
}

/** Set a React ref (callback or object ref) */
function setRef(ref: any, value: any): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) ref.current = value;
}

function shallowEqual(a: Record<string, any>, b: Record<string, any>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

function noop() {}
